import { useRPCContext } from "../../../contexts/RPCContext.js";
import { SelectMultiOptionWrapper } from "../../../components/Select/SelectMultiOptionWrapper.js";
import { useStateDebounced } from "../../../hooks/useStateDebounced.js";
import { useLocations } from "../hooks/useLocations.js";
import { createMockReactSyntheticEvent } from "../../../utils/mock-react-synthetic-event.js";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Icon, Image, Select } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { castArray } from "lodash-es";
import { MdLocationOff } from "react-icons/md";
//#region src/modules/Locations/shared/SelectSubLocation.tsx
var Base = ({ location }) => {
	if (!location) return null;
	const { address, name, primaryImageUrl } = location;
	return /* @__PURE__ */ jsxs("div", {
		className: "gap-x-sm flex items-center",
		children: [/* @__PURE__ */ jsx("div", {
			className: "shrink-0",
			children: /* @__PURE__ */ jsx(Image, {
				src: primaryImageUrl,
				className: "max-h-xl"
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "truncate",
			children: [/* @__PURE__ */ jsx("div", {
				className: "truncate text-xs",
				children: name
			}), /* @__PURE__ */ jsxs("div", {
				className: "text-color-subtle truncate text-xs",
				children: [address?.streetAddress && /* @__PURE__ */ jsxs("span", {
					className: "truncate font-medium",
					children: [
						address?.streetAddress,
						",",
						" "
					]
				}), address?.locality]
			})]
		})]
	});
};
var SelectedSingle = (location) => /* @__PURE__ */ jsx(Base, { location });
var SelectedMulti = (location, onRemove) => /* @__PURE__ */ jsx(SelectMultiOptionWrapper, {
	onRemove,
	children: /* @__PURE__ */ jsx("div", {
		className: "ml-xxs mr-xs max-w-full",
		children: /* @__PURE__ */ jsx(Base, { location })
	})
});
var ListItem = (locationOrSuggestion) => {
	if (locationOrSuggestion.placeId) return locationOrSuggestion.description;
	return /* @__PURE__ */ jsx(Base, { location: locationOrSuggestion });
};
var reducer = (state, { key, options, type }) => {
	if (type === "additional") {
		const _options = [...state.options, ...options];
		return {
			key,
			options: _options,
			total: _options.length
		};
	} else if (type === "reset") return {
		key,
		options,
		total: options.length
	};
};
var SelectSubLocation = ({ active = true, idFilter, isSearchable, limit = 25, multi = false, onChange, onChangeOptions, onChangeValue, parentLocationId, value, ...atalayaSelectProps }) => {
	const [, debouncedSearchText, setSearchText] = useStateDebounced("");
	const [selectedLocations, setSelectedLocations] = useState();
	const [response, dispatch] = useReducer(reducer, {
		key: "",
		options: [],
		total: 0
	});
	const [skip, setSkip] = useState(0);
	const { rpcService } = useRPCContext();
	const query = useMemo(() => ({
		active,
		idFilter,
		pagination: {
			limit,
			skip
		},
		parentLocationId,
		searchText: debouncedSearchText
	}), [
		active,
		debouncedSearchText,
		idFilter,
		limit,
		parentLocationId,
		skip
	]);
	const { data, loading } = useLocations(query);
	useEffect(() => {
		if (!data || response?.key === JSON.stringify(query)) return;
		if (Array.isArray(data?.locations)) dispatch({
			key: JSON.stringify(query),
			options: data.locations,
			type: skip ? "additional" : "reset"
		});
	}, [data]);
	useEffect(() => {
		if (!value) {
			setSelectedLocations([]);
			return;
		}
		const _value = castArray(value);
		const fetchedLocations = [];
		const idsToFetch = [];
		_value.forEach((selectedId) => {
			const fetchedLocation = response?.options.find((location) => location.id === selectedId);
			if (fetchedLocation) fetchedLocations.push(fetchedLocation);
			else idsToFetch.push(selectedId);
		});
		setSelectedLocations(fetchedLocations);
		if (idsToFetch.length) rpcService.Locations.GetLocationsList.v1.call({ idFilter: castArray(value) }).then((_response) => {
			setSelectedLocations([...fetchedLocations, ..._response.data.locations]);
		});
	}, [value]);
	const handleLoadOptions = useCallback(({ additional, text } = {}) => {
		if (text !== debouncedSearchText) {
			setSearchText(text || "");
			setSkip(0);
			return;
		}
		if (loading) return;
		if (additional && data?.total && data.total > response?.total) setSkip(skip + limit);
	}, [
		data?.total,
		debouncedSearchText,
		limit,
		loading,
		response?.total,
		skip
	]);
	const handleOnChange = async (option) => {
		let value;
		if (Array.isArray(option)) value = option.map((opt) => opt.id);
		else if (option) value = option.id;
		else if (multi) value = [];
		else value = null;
		if (onChangeValue) onChangeValue(value);
		if (onChangeOptions) onChangeOptions(option);
		if (onChange) onChange(createMockReactSyntheticEvent({ value }));
	};
	const NoOptions = () => /* @__PURE__ */ jsxs("div", {
		className: "gap-y-xs mt-sm flex flex-col items-center",
		children: [/* @__PURE__ */ jsx(Icon, {
			Icon: MdLocationOff,
			color: "subtle",
			size: "xxl"
		}), /* @__PURE__ */ jsx("span", {
			className: "text-color-subtle",
			children: "No Sub-Locations"
		})]
	});
	return /* @__PURE__ */ jsx(Select, {
		RenderListItem: ListItem,
		RenderSelectedMulti: SelectedMulti,
		RenderSelectedSingle: SelectedSingle,
		RenderNoOptions: NoOptions,
		isLoading: loading,
		isSearchable,
		loadOptions: handleLoadOptions,
		multi,
		optionLabelExtractor: ({ name }) => name,
		optionValueExtractor: ({ id }) => id,
		options: response?.options,
		value: selectedLocations,
		filterOption: null,
		onChangeOptions: handleOnChange,
		subText: atalayaSelectProps.subText,
		...atalayaSelectProps,
		allowCreate: false
	});
};
//#endregion
export { SelectSubLocation };

//# sourceMappingURL=SelectSubLocation.js.map